import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Calendar, Users, QrCode, MapPin } from "lucide-react";
import ShareEvent from "../ui/ShareEvent";
import { IOrganizationEvent } from "@/types/EventTypes";
import { partners } from "@/constants/partners";
import Sponsors from "./Sponsors";
import {
  formatDateRange,
  toAcronym,
  countSuppliers,
  getLocation,
} from "@/lib/utils";

interface EventCardProps {
  event: IOrganizationEvent;
  className?: string;
}

const EventCard = ({ event, className = "" }: EventCardProps) => {
  const [showQRModal, setShowQRModal] = useState(false);

  return (
    <>
      <div
        className={`relative bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 break-inside-avoid mb-8 cursor-pointer ${className}`}
      >
        <Link
          to="/events/$eventId"
          params={{ eventId: event.id }}
          className="absolute inset-0 z-0"
          aria-label={`View details for ${event.name}`}
        />

        {event.image_url ? (
          <img
            src={event.image_url}
            alt={event.name}
            className="w-full h-auto object-cover"
          />
        ) : (
          <div className="w-full h-48 flex items-center justify-center bg-gray-100 text-gray-700">
            <span className="text-5xl font-semibold tracking-wide">
              {toAcronym(event.name)}
            </span>
          </div>
        )}

        <div className="p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2 capitalize">
            {event.name}
          </h3>
          <p
            className="text-gray-600 mb-4 line-clamp-3"
            dangerouslySetInnerHTML={{ __html: event.description || "" }}
          />

          <div className="space-y-2 mb-4">
            <div className="flex items-center text-gray-500 text-sm">
              <Calendar size={16} className="mr-2" />
              <span>{formatDateRange(event.date_from, event.date_to)}</span>
            </div>
            <div className="flex items-center text-gray-500 text-sm">
              <Users size={16} className="mr-2" />
              <span>
                {countSuppliers(event.organization_event_attendees || [])}
              </span>
            </div>
            <div className="flex items-center text-gray-500 text-sm">
              <MapPin size={16} className="mr-2" />
              <span>
                {getLocation(event.organization_event_string_properties || [])}
              </span>
            </div>
          </div>

          <Sponsors partners={partners} />

          <div className="relative z-10 flex gap-2">
            <button
              type="button"
              onClick={() => setShowQRModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors duration-200 font-medium w-1/2"
              title="Show QR Code"
            >
              <QrCode size={20} />
              <span className="">Share</span>
            </button>
            <Link
              to="/events/$eventId"
              params={{ eventId: event.id }}
              className="flex-1 bg-primary-600 text-white text-center py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium w-1/2"
            >
              Details
            </Link>
          </div>
        </div>
      </div>

      {showQRModal && (
        <ShareEvent
          open={showQRModal}
          onOpenChange={(open) => !open && setShowQRModal(false)}
          event={event}
        />
      )}
    </>
  );
};

export default EventCard;
